import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Box, Chip, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Close, FlashOn, FlashOff, FlipCameraIos, Videocam, Stop, ScreenRotation } from '@mui/icons-material';

interface CameraCaptureProps {
  onCapture: (mediaSrc: string | Blob, newOrientation: 'portrait' | 'landscape', mediaType: 'image' | 'video') => void;
  onClose: () => void;
}

export default function CameraCapture({ onCapture, onClose }: CameraCaptureProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flashEnabled, setFlashEnabled] = useState(false);
  const [hasTorch, setHasTorch] = useState(true);
  const [capturedMediaCount, setCapturedMediaCount] = useState(0);
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>(isMobile ? 'portrait' : 'landscape');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const startCamera = useCallback(async () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: true
      };
      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = newStream;
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
      }
      setError(null);
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Camera not available');
    }
  }, [facingMode]);

  useEffect(() => {
    const checkCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        setHasMultipleCameras(videoDevices.length > 1);
      } catch (err) {
        console.error('Error enumerating devices:', err);
        setHasMultipleCameras(false);
      }
    };

    checkCameras();
    startCamera();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, [startCamera]);

  useEffect(() => {
    if (streamRef.current) {
      const videoTrack = streamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        // Check for torch capability with type assertion
        const capabilities = videoTrack.getCapabilities() as any;
        if (capabilities.torch) {
          setHasTorch(true);
        } else {
          setHasTorch(false);
        }
      }
      // Use type assertion for non-standard torch constraint
      videoTrack.applyConstraints({
        advanced: [{ torch: flashEnabled } as any]
      }).catch(error => {
        console.error('Error applying flash constraint:', error);
      });
    }
  }, [flashEnabled]);

  const capturePhoto = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        if (facingMode === 'user') {
          ctx.translate(canvas.width, 0);
          ctx.scale(-1, 1);
        }
        ctx.drawImage(video, 0, 0);
      }
      const imageSrc = canvas.toDataURL('image/jpeg');
      onCapture(imageSrc, orientation, 'image');
      setCapturedMediaCount(prev => prev + 1);
    }
  }, [onCapture, facingMode]);

  const toggleCamera = useCallback(() => {
    if (hasMultipleCameras) {
      setFacingMode(prevMode => prevMode === 'user' ? 'environment' : 'user');
    }
  }, [hasMultipleCameras]);

  const toggleFlash = useCallback(() => {
    setFlashEnabled(prev => !prev);
  }, []);

  const startRecording = useCallback(() => {
    if (streamRef.current) {
      const mediaRecorder = new MediaRecorder(streamRef.current, { mimeType: 'video/mp4' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/mp4' });
        onCapture(blob, orientation, 'video');
        setCapturedMediaCount(prev => prev + 1);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
    }
  }, [onCapture, orientation]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, []);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const toggleOrientation = useCallback(() => {
    setOrientation(prev => prev === 'portrait' ? 'landscape' : 'portrait');
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime(prevTime => prevTime + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  useEffect(() => {
    console.log(recordingTime)
      if (recordingTime > 29){
        stopRecording()
      }
  }, [recordingTime]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: isMobile ? '93vh' : '100vh',
        backgroundColor: '#141414',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        transform: 'translateZ(0)',
        willChange: 'transform',
      }}
    >
      {/* Top Bar */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 2,
          padding: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 100%)',
        }}
      >
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <Close />
        </IconButton>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {orientation === 'portrait' && (
            <Chip
              label={`${orientation.charAt(0).toUpperCase() + orientation.slice(1)} Mode`}
              color="primary"
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
              }}
            />
          )}
          {isRecording && (
            <Chip
              label={formatTime(recordingTime)}
              sx={{
                backgroundColor: 'rgba(255, 0, 0, 0.5)',
                color: 'white',
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={toggleFlash}
            sx={{ color: 'white' }}
            disabled={!hasTorch}
          >
            {flashEnabled ? <FlashOn /> : <FlashOff />}
          </IconButton>
        </Box>
      </Box>

      {/* Camera View */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
          borderRadius: '0 0 52px 52px',
          boxShadow: '0 2px 10px #000'
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
          }}
        />
      </Box>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      {orientation === 'landscape' &&
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: 20,
          transform: 'translateY(-50%) rotate(-90deg)',
          transformOrigin: 'left center',
          zIndex: 2,
        }}
      >
        <Chip
          label={`${orientation.charAt(0).toUpperCase() + orientation.slice(1)} Mode`}
          color="primary"
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            color: 'white',
          }}
        />
      </Box>
      }
      {/* Bottom Controls */}
      <Box
        sx={{
          bgcolor: '#141414',
          padding: '20px',
          paddingBottom: isMobile ? '40px' : '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          zIndex: -1
        }}
      >
        {/* Camera Controls Row */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
          }}
        >
          <IconButton
            onClick={toggleOrientation}
            disabled={isRecording}
            sx={{
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
            }}
          >
            <ScreenRotation />
          </IconButton>

          <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <IconButton
              onClick={capturePhoto}
              sx={{
                width: 70,
                height: 70,
                border: '4px solid white',
                borderRadius: '50%',
                p: 2,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  bgcolor: 'white',
                }}
              />
            </IconButton>

            {!isRecording ? (
              <IconButton
                onClick={startRecording}
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                }}
              >
                <Videocam />
              </IconButton>
            ) : (
              <IconButton
                onClick={stopRecording}
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(255, 0, 0, 0.5)',
                  '&:hover': { bgcolor: 'rgba(255, 0, 0, 0.7)' },
                }}
              >
                <Stop />
              </IconButton>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasMultipleCameras && (
              <IconButton onClick={toggleCamera} sx={{ color: 'white' }}>
                <FlipCameraIos />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>

      {error && (
        <Typography
          variant="h6"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: 'error.main',
            textAlign: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            padding: 2,
            borderRadius: 1,
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
}